/**
 * useEngagedPatients Hook - AWS API Version
 *
 * Fetches patients who engaged (read secure message) but haven't been booked.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { api } from '../lib/api';
import { normalizeStatus } from '../utils/statuses';
import dayjs from 'dayjs';

const NUDGE_THRESHOLD_HOURS = 48;

export const useEngagedPatients = () => {
  const [engagementLogs, setEngagementLogs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEngagedPatients = useCallback(async () => {
    setLoading(true);

    try {
      // Fetch engagement logs from API
      const logsResponse = await api.getEngagementLogs();
      const logs = logsResponse.logs || logsResponse || [];

      if (logs.length === 0) {
        setEngagementLogs([]);
        setPatients([]);
        setLoading(false);
        return;
      }

      // Get unique patient IDs from logs
      const patientIds = [...new Set(logs.map(log => log.patient_id))];

      // Fetch patient data
      const patientsResponse = await api.getPatients();
      const allPatients = patientsResponse.patients || patientsResponse || [];

      // Filter to only patients in the engagement logs
      const engagedPatientData = allPatients.filter(p => patientIds.includes(p.id));

      setEngagementLogs(logs);
      setPatients(engagedPatientData);
    } catch (error) {
      console.error('Failed to fetch engaged patients:', error);
      setEngagementLogs([]);
      setPatients([]);
    }

    setLoading(false);
  }, []);

  // Calculate engaged but not booked patients
  const engagedNotBookedPatients = useMemo(() => {
    if (!engagementLogs.length || !patients.length) return [];

    // Create a map of patient_id -> earliest engagement
    const engagementMap = new Map();
    engagementLogs.forEach(log => {
      const existing = engagementMap.get(log.patient_id);
      if (!existing || dayjs(log.created_at).isBefore(dayjs(existing.created_at))) {
        engagementMap.set(log.patient_id, log);
      }
    });

    // Filter patients: engaged + not closed
    return patients
      .filter(patient => {
        const ns = normalizeStatus(patient.status);
        const isResolved = ns === 'recovered' || ns === 'closed';
        return !isResolved && engagementMap.has(patient.id);
      })
      .map(patient => {
        const engagement = engagementMap.get(patient.id);
        const engagedAt = dayjs(engagement.created_at);
        const hoursSinceEngagement = dayjs().diff(engagedAt, 'hour');
        const daysSinceEngagement = dayjs().diff(engagedAt, 'day');

        return {
          ...patient,
          engagedAt: engagement.created_at,
          hoursSinceEngagement,
          daysSinceEngagement,
          needsNudge: hoursSinceEngagement >= NUDGE_THRESHOLD_HOURS,
        };
      })
      .sort((a, b) => {
        // Sort by needsNudge first, then by hours since engagement (oldest first)
        if (a.needsNudge && !b.needsNudge) return -1;
        if (!a.needsNudge && b.needsNudge) return 1;
        return b.hoursSinceEngagement - a.hoursSinceEngagement;
      });
  }, [engagementLogs, patients]);

  // Stats for display
  const stats = useMemo(() => {
    const needsNudge = engagedNotBookedPatients.filter(p => p.needsNudge);
    const avgDays = needsNudge.length > 0
      ? Math.round(needsNudge.reduce((sum, p) => sum + p.daysSinceEngagement, 0) / needsNudge.length)
      : 0;

    return {
      total: engagedNotBookedPatients.length,
      needsNudge: needsNudge.length,
      avgDaysSinceEngagement: avgDays,
    };
  }, [engagedNotBookedPatients]);

  useEffect(() => {
    fetchEngagedPatients();
  }, [fetchEngagedPatients]);

  return {
    engagedNotBookedPatients,
    loading,
    refetch: fetchEngagedPatients,
    stats,
  };
};
